import { ChangeDetectorRef, Component, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Subject } from 'rxjs';
import { combineLatest, filter, first, takeUntil } from 'rxjs';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import { filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { RequisitionsParams } from '@core/requisitions-types';
import { RequisitionsScope, RequisitionsListParam } from '@core/requisitions-types';
import { provideIntersectionObserverArea } from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';
import { BulkActionsController, BulkActionsSelectionTracker } from '@recruitee/bulk-actions-legacy';
import { RequisitionsSelectors } from '@recruitee/requisitions-data';
import type { RequisitionsFilterCounters } from '@recruitee/requisitions-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

import { RequisitionsAnalytics } from '../../analytics/requisitions.analytics';
import { RequisitionsListTableViewContainerComponent } from '../../containers/list/requisitions-list-table-view-container/requisitions-list-table-view-container.component';
import { RequisitionsListTilesViewContainerComponent } from '../../containers/list/requisitions-list-tiles-view-container/requisitions-list-tiles-view-container.component';
import { RequisitionsListToolbarContainerComponent } from '../../containers/list/requisitions-list-toolbar-container/requisitions-list-toolbar-container.component';
import { RequisitionsListDataService } from '../../data/list/requisitions-list-data.service';
import { RequisitionsListFacade } from '../../facades/list/requisitions-list.facade';
import type { RequisitionsGroupToDisplay } from '../../requisitions.types';
import { RequisitionsListTab, RequisitionsListViewType } from '../../requisitions.types';
import { RequisitionsBulkActionsExecutorService } from '../../services/requisitions-bulk-actions-executor.service';
import { RequisitionsBulkActionsController } from '../../services/requisitions-bulk-actions.controller';
import { RequisitionsCreateService } from '../../services/requisitions-create.service';
import { RequisitionsListCacheService } from '../../services/requisitions-list-cache.service';
import { RequisitionsListColumnsModelService } from '../../services/requisitions-list-columns-model.service';
import { RequisitionsListGroupsService } from '../../services/requisitions-list-groups.service';
import { RequisitionsListTableService } from '../../services/requisitions-list-table.service';
import { RequisitionsListViewStateService } from '../../services/requisitions-list-view-state.service';
import { RequisitionsNotificationsService } from '../../services/requisitions-notifications.service';
import { RequisitionsSharedActionsService } from '../../services/requisitions-shared-actions.service';
import { RequisitionsTutorialDialogService } from '../../services/requisitions-tutorial-dialog.service';

@Component({
  selector: 'rt-requisitions-list-page',
  templateUrl: './list-page.component.html',
  styleUrls: ['./list-page.component.less'],
  imports: [
    RequisitionsListTilesViewContainerComponent,
    RequisitionsListToolbarContainerComponent,
    RequisitionsListTableViewContainerComponent,
    RouterModule,
    TranslatePipe,
    InfoboxComponent,
  ],
  providers: [
    RequisitionsCreateService,
    RequisitionsListDataService,
    RequisitionsListFacade,
    RequisitionsListCacheService,
    RequisitionsSharedActionsService,
    RequisitionsListViewStateService,
    RequisitionsListGroupsService,
    RequisitionsAnalytics,
    RequisitionsListColumnsModelService,
    RequisitionsListTableService,
    provideAnalyticsViewContext(AnalyticsViewContext.Requisitions),
    ...provideIntersectionObserverArea({
      intersectionOptions: {
        rootMargin: '0px 0px 300px 0px',
        threshold: [0.0, 1.0],
      },
    }),
    BulkActionsSelectionTracker,
    RequisitionsBulkActionsController,
    RequisitionsBulkActionsExecutorService,
    { provide: BulkActionsController, useExisting: RequisitionsBulkActionsController },
  ],
})
export class ListPageComponent {
  public RequisitionsListViewType: typeof RequisitionsListViewType = RequisitionsListViewType;

  public requisitionsGroups: RequisitionsGroupToDisplay[] = [];
  public isDefaultFlowMissing = false;
  public isLoaded = false;
  public params: RequisitionsParams;
  public filterCounters: RequisitionsFilterCounters;
  public activeTab: RequisitionsListTab;
  public hasRequisitions: boolean;
  public hasCreatedFlows: boolean;
  public viewType: RequisitionsListViewType;
  public collapsedGroups: string[] = [];
  public showGroups: boolean;
  public isEmptyScope: boolean;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private store: Store,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    protected requisitionsDataService: RequisitionsListDataService,
    protected requisitionsNotifications: RequisitionsNotificationsService,
    protected requisitionsListFacade: RequisitionsListFacade,
    private requisitionsViewState: RequisitionsListViewStateService,
    private requisitionsListGroupsService: RequisitionsListGroupsService,
    private tutorialDialogService: RequisitionsTutorialDialogService,
    private vcRef: ViewContainerRef,
  ) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        first(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.requisitionsViewState.initializeQueryParamsFromCache();
      });
  }

  public ngOnInit() {
    this.tutorialDialogService.openIntroDialogIfNeeded(this.vcRef);

    this.requisitionsDataService.data$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisitionsGroups, defaultFlowIsMissing, params }) => {
        if (params[RequisitionsListParam.GroupBy]) {
          this.requisitionsGroups =
            this.requisitionsListGroupsService.fillGroupNamesWithTranslations(
              requisitionsGroups,
              params[RequisitionsListParam.GroupBy],
            );
        } else {
          this.requisitionsGroups = requisitionsGroups;
        }
        this.hasCreatedFlows = this.requisitionsListFacade.hasCreatedFlows;
        this.isDefaultFlowMissing = defaultFlowIsMissing;
        this.cdRef.detectChanges();
      });

    this.listenToViewStateChanges();
    this.listenToParamsChanges();
    this.listenToFilterCountersChanges();
    this.listenToLoadedStateChanges();
    this.listenToAllActiveRequisitionsChanges();
    this.listenToRequisitionFeatureDisable();
  }

  public get isMissingDefaultFlowInfoboxVisible(): boolean {
    return (
      this.isDefaultFlowMissing &&
      this.activeTab === RequisitionsListTab.Active &&
      this.hasRequisitions &&
      this.hasCreatedFlows
    );
  }

  private getActiveTab(): RequisitionsListTab {
    switch (this.params.scope) {
      case RequisitionsScope.Archived:
        return RequisitionsListTab.Archived;
      case RequisitionsScope.Active:
      case RequisitionsScope.NeedMyApproval:
      case RequisitionsScope.RequestedByMe:
      default:
        return RequisitionsListTab.Active;
    }
  }

  private listenToFilterCountersChanges(): void {
    this.requisitionsDataService.filterCounters$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(counters => {
        this.filterCounters = counters;
        this.cdRef.detectChanges();
      });
  }

  private listenToLoadedStateChanges(): void {
    this.requisitionsDataService.isLoaded$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(isLoaded => {
        this.isLoaded = isLoaded;
        this.cdRef.detectChanges();
      });
  }

  private listenToViewStateChanges(): void {
    this.requisitionsViewState.viewState$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(state => {
      this.viewType = state.viewType;
      this.collapsedGroups = state.collapsedGroups;
      this.cdRef.detectChanges();
    });
  }

  private listenToParamsChanges(): void {
    this.requisitionsDataService.params$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(params => {
        this.params = params;
        this.showGroups = !!params[RequisitionsListParam.GroupBy];
        this.activeTab = this.getActiveTab();
        this.isEmptyScope = this.getIsEmptyScope();
        this.cdRef.detectChanges();
      });
  }

  private listenToAllActiveRequisitionsChanges(): void {
    this.store
      .select(RequisitionsSelectors.allActive())
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(reqs => {
        this.hasRequisitions = !!reqs.length;
        this.cdRef.detectChanges();
      });
  }

  private listenToRequisitionFeatureDisable() {
    combineLatest([
      this.store.select(RequisitionsConfigSelectors.requisitionsFeatureEnabled),
      this.store.select(RequisitionsConfigSelectors.isLoaded).pipe(filterBoolean),
    ])
      .pipe(
        filter(([isEnabled]) => !isEnabled),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.router.navigate(['/dashboard']));
  }

  private getIsEmptyScope(): boolean {
    if (!this.filterCounters) return false;

    switch (this.params.scope) {
      case RequisitionsScope.Active:
        return !this.filterCounters.active;
      case RequisitionsScope.NeedMyApproval:
        return !this.filterCounters.needMyApproval;
      case RequisitionsScope.RequestedByMe:
        return !this.filterCounters.requestedByMe;
      case RequisitionsScope.Archived:
      default:
        return false;
    }
  }
}
